import { supabase } from "./supabase";

const ALLOWED_DOMAIN = import.meta.env.VITE_EMAIL_DOMAIN || "gitbooked.com";

export function formatBusinessEmail(businessName: string): string {
  // Convert business name to lowercase and replace spaces with dots
  const formattedName = businessName
    .toLowerCase()
    .replace(/\s+/g, ".")
    .replace(/[^a-z0-9.]/g, "");

  return `${formattedName}@${ALLOWED_DOMAIN}`;
}

export function validateBusinessEmail(email: string): boolean {
  // Check if email ends with the allowed domain
  return email.endsWith(`@${ALLOWED_DOMAIN}`);
}

export async function updateBusinessEmail(
  businessId: string,
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!validateBusinessEmail(email)) {
      return {
        success: false,
        error: `Email must end with @${ALLOWED_DOMAIN}`,
      };
    }

    if (!supabase) {
      return {
        success: false,
        error: "Supabase client is not initialized",
      };
    }

    const { error } = await supabase
      .from("businesses")
      .update({ business_domain_email: email })
      .eq("id", businessId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error updating business email:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to update business email",
    };
  }
}

interface SendEmailParams {
  to: string;
  subject: string;
  text: string;
  html?: string;
  businessId: string;
  from?: string; // Optional - will get business email if not provided
  conversationId?: string; // Optional - for tracking in communications
}

interface SendEmailResult {
  success: boolean;
  error?: string;
}

/**
 * Sends an email using the application's email system
 * Uses SendGrid via Supabase Edge Functions
 */
export async function sendEmail({
  to,
  subject,
  text,
  html,
  businessId,
  from,
  conversationId
}: SendEmailParams): Promise<SendEmailResult> {
  try {
    if (!supabase) {
      throw new Error("Supabase client is not initialized");
    }

    // If from email not provided, get the business email
    let fromEmail = from;
    if (!fromEmail) {
      const { data: business, error: businessError } = await supabase
        .from("businesses")
        .select("business_domain_email")
        .eq("id", businessId)
        .single();

      if (businessError) throw businessError;
      if (!business?.business_domain_email) {
        throw new Error("Business email not configured");
      }

      fromEmail = business.business_domain_email;
    }

    // Send email via Supabase Edge Function
    const { error } = await supabase.functions.invoke("send-email", {
      body: {
        to,
        subject,
        text,
        html,
        from: fromEmail,
        businessId
      },
    });

    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error("Error sending email:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Failed to send email",
    };
  }
}

/**
 * Simple Gmail email sending function for use throughout the app
 * Uses the Gmail edge function similar to how sendSMS works
 */
export interface SendGmailEmailParams {
  businessId: string;
  to: string;
  subject?: string;
  body: string;
  aiGenerated?: boolean;  // Add AI flag parameter
  attachments?: Array<{
    filename: string;
    content: string;  // base64 encoded content
    type: string;     // MIME type like 'application/pdf'
  }>;
}

export interface SendGmailEmailResult {
  success: boolean;
  error?: string;
  messageId?: string;
}

export async function sendGmailEmail({
  businessId,
  to,
  subject,
  body,
  aiGenerated = false,  // Default to false if not provided
  attachments
}: SendGmailEmailParams): Promise<SendGmailEmailResult> {
  try {
    console.log('📧 Sending Gmail email:', { to, subject, businessId, aiGenerated, hasAttachments: !!attachments });

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gmail-send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject: subject || '',
        body,
        businessId,
        aiGenerated,  // Pass the AI flag to the edge function
        attachments   // Pass attachments to the edge function
      })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('❌ Gmail send failed:', data.error);
      return {
        success: false,
        error: data.error || 'Failed to send email'
      };
    }

    console.log('✅ Gmail email sent successfully:', data.messageId);
    return {
      success: true,
      messageId: data.messageId
    };
  } catch (error) {
    console.error('❌ Error sending Gmail email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email'
    };
  }
}
